import ReportAdminReportCard from './report-admin-report-card'
import { Add, ArrowBack, Search } from '@mui/icons-material'
import {
  Box,
  Hidden,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import { CreateIncidentReport } from 'components/incident-reports'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { IncidentReport } from 'types/incident-report'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  incidentReport: IncidentReportPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    incidentReport: IncidentReportOperations
  }
}

interface OwnProps {
  onCloseContent: () => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const ReportAdminFC: React.FC<any> = (props: AppProps) => {
  const { user, theme, incidentReport, operations, onCloseContent } = props
  const [selectedReport, setSelectedReport] = React.useState<IncidentReport>()
  const [searchKey, setSearchKey] = React.useState('')
  const [filtered, setFiltered] = React.useState(false)
  const isMobileView = useMediaQuery('(max-width:600px)')

  const _filterData = (): void => {
    void get(`/report/?search=${searchKey}`).then((data: IncidentReport[]) => {
      try {
        const parsedData = data.map(report => {
          return objectifyDateStringFields(report)
        })
        operations.incidentReport.set(parsedData)
        operations.incidentReport.loading(false)
        setFiltered(searchKey.trim() !== '')
      } catch (e) {
        console.log(e)
      }
    })
  }

  const emptyReport: IncidentReport = {
    id: 0,
    tracked: false,
    title: '',
    status: '',
    description: '',
    descriptionRte: '',
    createdBy: user,
    managedBy: user,
    updatedBy: user,
    createdAt: new Date(),
    updatedAt: new Date(),
    comments: [],
    requiredAction: 'Repair',
    images: []
  }

  React.useEffect(() => {
    if (incidentReport.data.length < 1) {
      operations.incidentReport.loading(true)
      void get('/report/').then((data: IncidentReport[]) => {
        const parsedData = data.map(report => {
          return objectifyDateStringFields(report)
        })
        operations.incidentReport.set(parsedData)
        operations.incidentReport.loading(false)
      })
    }
  }, [])

  return (
    <Box
      border={1}
      borderRadius={'12px'}
      borderColor={theme.secondaryColor}
      padding={isMobileView ? 1 : 2}
      width={0.9}
      minHeight={isMobileView ? '95vh' : '85vh'}
      marginX={'auto'}
    >
      <Box display={'flex'} alignItems={'center'}>
        <IconButton
          size={'medium'}
          onClick={() => {
            // Fetch new if current results are filtered
            if (filtered) {
              void get('/report/').then(data => {
                try {
                  // TODO: Use Report APIs
                } catch (e) {
                  console.log(e)
                }
              })
            }
            onCloseContent()
          }}
        >
          <ArrowBack fontSize={'large'} />
        </IconButton>
        <Box
          paddingLeft={1}
          borderRight={1}
          height={'56px'}
          color={theme.secondaryColor}
        />
        <Box
          paddingRight={2}
          borderLeft={1}
          height={'56px'}
          color={theme.secondaryColor}
        />
        <Hidden smDown>
          <Typography variant={'h3'}>Reports Admin</Typography>
        </Hidden>
        <Box flexGrow={1} />
        <Box>
          <TextField
            id={'search-report-name'}
            type={'text'}
            placeholder={'Report name...'}
            value={searchKey}
            onChange={event => setSearchKey(event.currentTarget.value)}
            onKeyDown={event => {
              if (event.code === 'Enter') {
                _filterData()
              }
            }}
            InputProps={{
              startAdornment: <Search />
            }}
          />
        </Box>
        <Tooltip title={'Add new report'} placement={'top'}>
          <IconButton
            sx={{ margin: 1 }}
            onClick={() => setSelectedReport(emptyReport)}
          >
            <Add fontSize={'large'} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box padding={isMobileView ? 1 : 4}>
        <CreateIncidentReport
          noButton
          selectedReport={selectedReport}
          setSelectedReport={setSelectedReport}
        />
        <Box
          display={'flex'}
          flexWrap={'wrap'}
          justifyContent={isMobileView ? 'center' : 'flex-start'}
        >
          {incidentReport.data.map((item, index) => (
            <ReportAdminReportCard
              key={index}
              reportDetails={item}
              onClickEvent={reportDetail => setSelectedReport(reportDetail)}
            />
          ))}
          {incidentReport.data.length === 0 && (
            <Typography variant="h3" align="center" marginY={2}>
              No Data
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    incidentReport: state.incidentReport
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  incidentReport: incidentReportOperations
})

const ReportAdmin = connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportAdminFC) as React.FC<OwnProps>

export default ReportAdmin
